import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteFeatureType } from '../hooks/api/use-feature-types.hook';
import { FeatureTypeApiModel } from '../../../types/models/feature-type.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchFeatureTypes } from '../api/feature-types.api';

export const FeatureTypes = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteFeatureType, { loading: deletingFeatureType, error: deleteError }] = useDeleteFeatureType();

  const columns: ColDef<FeatureTypeApiModel>[] = [
    {
      field: 'name',
      colId: 'displayName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'value',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<FeatureTypeApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteFeatureType({ featureTypeId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Feature Type',
                message: `Are you sure you want to delete feature type "${node.data?.name}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getFeatureTypes: Parameters<typeof InfniteScrollingGrid<FeatureTypeApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchFeatureTypes({
      ...filterQuery,
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Feature Types'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Feature Types</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Feature Type
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingFeatureType} columnDefs={columns} onGetRows={getFeatureTypes} />
        </Box>
      </Box>
    </Box>
  );
};
